'use client';

import { type PropsWithChildren, useEffect, useState } from 'react';
import { QueryClientProvider } from 'react-query';
import dynamic from 'next/dynamic';
import { initializeApp2WebInterface } from '@kcd/app-interface';
import {
  FeatherConfig,
  ToastContainer,
  ToastContextProvider,
} from '@kcd/design-system';
import { initializeTrackerInterface } from '@kcd/event-tracker';

import { useHistoryIndexChangeByRoute } from '~/app/hooks/useGoBack';
import { createReactQueryClient } from '~/libs/reactQuery';
import { datadogInitialize } from '~/utils/datadog';
import { ENV } from '~/utils/env';
import { useInitializeKakao } from '~/utils/kakao';

const ReactQueryDevtools = dynamic(
  () =>
    import('react-query/devtools/development').then(
      (mod) => mod.ReactQueryDevtools,
    ),
  { ssr: false },
);

/**
 * @description 전역으로 처리가 필요한 것 (ex. 이벤트 트래커, 데이터독, 앱 인터페이스 등)들을 처리함
 * */
export function AppRootProvider({ children }: PropsWithChildren) {
  const [queryClient] = useState(() => createReactQueryClient());
  useHistoryIndexChangeByRoute();

  useEffect(() => {
    initializeApp2WebInterface();

    // local과 배포된것을 비교
    if (process.env.NODE_ENV === 'production') {
      initializeTrackerInterface({
        greenfinchConfig: {
          serviceName: 'kcd_cashnote',
          writeKey: (ENV === 'prod'
            ? process.env.NEXT_PUBLIC_GREENFINCH_WRITE_KEY
            : process.env.NEXT_PUBLIC_GREENFINCH_STAGING_WRITE_KEY) as string,
          debug: ENV !== 'prod',
        },
      });
    }
    if (ENV === 'prod') {
      datadogInitialize();
    }
  }, []);

  useEffect(() => {
    if (ENV === 'prod') return;
    void (async () => {
      const eruda = await import('eruda');
      eruda.default.init({
        tool: ['console', 'network', 'resources', 'info'],
      });
    })();
  }, []);

  useInitializeKakao();

  return (
    <QueryClientProvider client={queryClient}>
      <FeatherConfig>
        <ToastContextProvider>
          {children}
          <ToastContainer
            style={{ zIndex: 20 }}
            portalTargetSelector="#toast"
          />
        </ToastContextProvider>
      </FeatherConfig>
      {process.env.NEXT_PUBLIC_APP_ENV !== 'prod' && (
        <ReactQueryDevtools position="bottom-left" initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}
